import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Users, Package, BarChart3, Settings, LogOut, Send, Calendar, ClipboardCheck, Megaphone, UserCog, Mail, UserPlus, MailPlus, Clock, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/utils/api';

export const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState(null);
  const [userRole, setUserRole] = useState('');
  
  useEffect(() => {
    fetchPermissions();
  }, []);
  
  const fetchPermissions = async () => {
    try {
      const response = await api.get('/auth/permissions');
      setPermissions(response.data.permissions);
      setUserRole(response.data.role);
    } catch (error) {
      console.error('Failed to fetch permissions');
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };
  
  // Define menu items with required permissions
  const allMenuItems = [
    { path: '/dashboard', label: 'Dashboard', icon: Home, permission: null }, // Always visible
    { path: '/pending-schedule-invitation', label: 'Pending Schedule Invitation', icon: Clock, permission: 'can_view_families' },
    { path: '/new-families', label: 'New Families', icon: UserPlus, permission: 'can_view_families' },
    { path: '/families', label: 'All Families', icon: Users, permission: 'can_view_families' },
    { path: '/bulk-invitation', label: 'Invite by Email', icon: MailPlus, permission: 'can_send_invitations' },
    { path: '/scheduling-invitations', label: 'Send Invitations', icon: Send, permission: 'can_send_invitations' },
    { path: '/pending-schedules', label: 'Pending Schedules', icon: ClipboardCheck, permission: 'can_confirm_deliveries' },
    { path: '/scheduled-deliveries', label: 'Confirmed Deliveries', icon: Calendar, permission: 'can_manage_deliveries' },
    { path: '/inventory', label: 'Inventory', icon: Package, permission: 'can_manage_inventory' },
    { path: '/announcements', label: 'Announcements', icon: Megaphone, permission: 'can_send_announcements' },
    { path: '/email-logs', label: 'Email Logs', icon: Mail, permission: 'can_send_announcements' },
    { path: '/reports', label: 'Reports', icon: BarChart3, permission: 'can_view_reports' },
    { path: '/users', label: 'User Management', icon: UserCog, permission: 'can_view_users' },
    { path: '/settings', label: 'Settings', icon: Settings, permission: null }, // Always visible
  ];
  
  // Filter menu items based on permissions
  const menuItems = allMenuItems.filter(item => {
    if (!item.permission) return true; // No permission required
    if (!permissions) return false; // Still loading
    return permissions[item.permission];
  });
  
  // Role display names
  const roleDisplayNames = {
    admin: 'Administrator',
    staff: 'Staff (Level 2)',
    volunteer: 'Volunteer',
    family: 'Family'
  };
  
  return (
    <div className="fixed left-0 top-0 h-screen w-64 bg-background-paper border-r border-stone-200 flex flex-col" data-testid="admin-sidebar">
      <div className="p-6">
        <h1 className="text-2xl font-bold text-primary" data-testid="sidebar-logo">The Bookshelf Project</h1>
        {userRole && (
          <p className="text-xs text-stone-500 mt-1" data-testid="user-role-badge">
            {roleDisplayNames[userRole] || userRole}
          </p>
        )}
      </div>
      
      <nav className="flex-1 px-4 space-y-2 overflow-y-auto pb-4">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
          return (
            <Link key={item.path} to={item.path}>
              <div
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive
                    ? 'bg-primary text-white'
                    : 'text-stone-600 hover:bg-stone-100 hover:text-primary'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </div>
            </Link>
          );
        })}
      </nav>
      
      <div className="p-4 border-t border-stone-200 space-y-2 flex-shrink-0">
        <a
          href="https://www.thebookshelfproject.org"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 px-4 py-2 rounded-lg text-sm text-stone-500 hover:text-primary hover:bg-stone-100 transition-colors"
          data-testid="back-to-main-site-link"
        >
          <ExternalLink className="w-4 h-4" />
          <span>Back to main site</span>
        </a>
        <Button
          data-testid="logout-button"
          onClick={handleLogout}
          variant="ghost"
          className="w-full justify-start text-stone-600 hover:text-primary hover:bg-stone-100"
        >
          <LogOut className="w-5 h-5 mr-3" />
          Logout
        </Button>
      </div>
    </div>
  );
};

export default Sidebar;